import { Analytics, checkPlacement } from 'client/Global/Analytics'
import { trackingFields } from 'client/Global/AnalyticsEvents'
import { Segment } from 'client/Global/Segment'
import { SegmentEvents } from 'client/Global/SegmentEvents'
import { getLocation } from 'client/globalWindow'
import { UiSetup } from 'client/Interfaces/CompanyInterface'
import { l } from 'client/Other/Translation'

import { DocumentTypes } from '../Interfaces/Draft/DraftInterface'
import { type ModalInterface } from '../Interfaces/ModalInterface'
import { type NavigationInterface } from '../Interfaces/NavigationInterface'
import { type StateInterface } from '../Interfaces/StateInterface'
import { hasModuleAvailable, moduleNames } from '../reducers/settings'
import { logout } from '../utils'
import { actionCreator } from './'
import { actionCreatorAsync } from './index'

export const navigateAddModal = actionCreator<ModalInterface>('NAVIGATE_ADD_MODAL')
export const navigateRemoveModal = actionCreator<string>('NAVIGATE_REMOVE_MODAL')
export const navigateChangeModal = actionCreator<ModalInterface>('NAVIGATE_CHANGE_MODAL')
export const setNavigationPath = actionCreator<string>('SET_NAVIGATION_PATH')
export const setInvoiceEditorLoading = actionCreator('SET_INVOICE_EDITOR_LOADING')
export const setInvoiceEditorLoaded = actionCreator('SET_INVOICE_EDITOR_LOADED')

export const getPath = (state: StateInterface, payload: NavigationInterface): string[] => {
  let path = ['']
  if (state?.navigation?.path) {
    path = state.navigation.path.split('/')
  }

  if (payload?.path) {
    path = payload.path.split('/')
  } else if (['receipts', 'deliverynotes', 'editor'].includes(path[1])) {
    return path
  } else if (payload?.app?.mod) {
    path = ['', payload.app.mod]
    if (payload.app.view) {
      path.push(payload.app.view)
    }
  }
  return path
}

export const doLogout = (uiSetup?: UiSetup) => logout(false, uiSetup)

export const navigateToInvoiceById = (id: number): void => {
  Analytics.trackEvent(trackingFields.name.notificationLink, {
    type: trackingFields.type.action,
    category: trackingFields.category.notification,
    result: trackingFields.result.click,
    placement: checkPlacement(window.location.pathname),
    link: 'viewInvoice',
  })
  getLocation().assign(`/reactInvoiceList/invoice/${id}`)
}

export const navigateToEstimateById = (id: number): void => {
  Analytics.trackEvent(trackingFields.name.notificationLink, {
    type: trackingFields.type.action,
    category: trackingFields.category.notification,
    result: trackingFields.result.click,
    placement: checkPlacement(window.location.pathname),
    link: 'viewEstimate',
  })
  getLocation().assign(`/reactEstimateList/estimate/${id}`)
}

export const navigateToRecurringProfile = (profileId: number): void => {
  Analytics.trackEvent(trackingFields.name.notificationLink, {
    type: trackingFields.type.action,
    category: trackingFields.category.notification,
    result: trackingFields.result.click,
    placement: checkPlacement(window.location.pathname),
    link: 'viewRecurringProfile',
  })
  getLocation().assign(`/reactRecurringProfiles/${profileId}`)
}

export const navigateToMyPlan = (): void => {
  Analytics.trackEvent(trackingFields.name.notificationLink, {
    type: trackingFields.type.action,
    category: trackingFields.category.notification,
    result: trackingFields.result.click,
    placement: checkPlacement(window.location.pathname),
    link: 'viewSubscription',
  })
  getLocation().assign('/myAccount/myPlan')
}

export const navigateToBankingPage = (): void => {
  Analytics.trackEvent(trackingFields.name.notificationLink, {
    type: trackingFields.type.action,
    category: trackingFields.category.notification,
    result: trackingFields.result.click,
    placement: checkPlacement(window.location.pathname),
    link: 'viewBankingPage',
  })
  getLocation().assign('/banking')
}

export const navigateToInvoiceList = (filter?) => {
  return async (dispatch, getState) => {
    const { settings } = getState()
    const hasReactList = hasModuleAvailable(settings, moduleNames.reactInvoiceList)
    const path = hasReactList ? '/reactInvoiceList' : '/invoices'
    const mod = path.replace('/', '')
    const navParams: NavigationInterface = {
      path,
      app: {
        mod,
        'tabs-invoices': 'invoices',
        ...(filter ? { filter } : {}),
      },
    }
    return dispatch(setNavigation(navParams))
  }
}

export const loadingEditorVitals = (vitalsLoader) => {
  return async (dispatch) => {
    dispatch(setInvoiceEditorLoading())
    await vitalsLoader(dispatch)
    dispatch(setInvoiceEditorLoaded())
  }
}

function setWindowTitle(title: string) {
  if (/invoice/i.test(title) && title !== 'invoiceFinancing') {
    // a fix to make it generic "Invoices"
    title = 'reactInvoiceList'
  }
  let translation = 'Zervant - ' + l('history.title.' + title)
  if (!title || translation.includes('history.title')) {
    // not found translations will be ignored
    translation = 'Zervant'
  }
  document.title = translation
}

export const setNavigation = (navigation: NavigationInterface, uiSetup?: UiSetup) => {
  if (navigation.app) {
    setWindowTitle(navigation.app.mod)
  }
  return async (dispatch, getState) => {
    await dispatch(
      actionCreatorAsync('SET_NAVIGATION', async () => {
        const state = getState()
        const path = getPath(state, navigation)

        if (
          state.draft?.isDirty &&
          navigation.path &&
          !['/reactInvoice', '/DraftPreview', '/EstimatePreview'].includes(navigation.path) &&
          state.draft.document !== DocumentTypes.CREDITNOTE
        ) {
          return await new Promise((resolve) => {
            dispatch(
              navigateAddModal({
                component: 'LeaveUnsavedModal',
                props: {
                  payload: navigation,
                },
              }),
            ).then(resolve)
          })
        }

        if (navigation.path === '/logout') {
          setTimeout(async () => {
            await doLogout(uiSetup)
          }, 2000)

          return dispatch(
            navigateAddModal({
              component: 'LoadingModal',
              props: {
                header: l('Logout'),
                text: l('logout.Title'),
              },
            }),
          )
        }

        const payload = { ...navigation }
        payload.path = path.join('/')

        payload.app.mod = path[1]
        if (path.length > 2 && path[2] !== '') {
          payload.app.view = path[2]
        }
        setWindowTitle(navigation.app.mod)

        return payload
      }),
    )
  }
}
